import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, switchMap, takeUntil, withLatestFrom } from 'rxjs';
import { TcsUserSettingsService } from 'src/lib/tcs-user-settings/tcs-user-settings.service';
import { UserEmailService } from 'src/lib/tcs-identity/user-email.service';
import { generateNamedMD5Hash } from 'src/util/hash';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { SessionStorage } from 'src/util/storage/session-storage';
import { Executor } from 'src/util/executor';
import { TickService } from '../tick.service';
import { AppInsightsLogger } from './app-insights-logger';
import { LoggerOptions, LoggerOptionsService } from './logger-options.service';
import { NewRelicLogger } from './new-relic-logger';

const createStorageId = (id: string) => generateNamedMD5Hash('cache-logger', [id]);

export enum LogType {
  Information = 'info',
  Warning = 'warn',
  Error = 'error',
}

interface CacheItem {
  type: LogType;
  message: string;
  properties?: { [name: string]: string };
  timestamp: Date;
}

@Injectable({
  providedIn: 'root',
})
export class CacheLoggerCacheService implements OnDestroy {
  private _exceededQuotaMaxAgeMs = 1000 * 60 * 60 * 4;
  private _maxAgeMs = 1000 * 60 * 60 * 8;
  private _id$ = new BehaviorSubject<string>('');
  private _onDestroy$ = new Subject();
  private _cache$ = new BehaviorSubject<CacheItem[]>([]);
  private _cacheLoggerStorage: SessionStorage;

  constructor(
    private _optionsManager: LoggerOptionsService,
    private _insightsLogger: AppInsightsLogger,
    private _newRelicLogger: NewRelicLogger,
    userSettings: TcsUserSettingsService,
    userEmailService: UserEmailService,
    tickService: TickService,
  ) {
    this._cacheLoggerStorage = new SessionStorage({
      name: 'CacheLogger',
      setItemExecutor: new Executor({
        strategies: [
          // execute operation
          {
            name: 'operation',
            canRetry: (args) => args.errorName === 'QuotaExceededError',
            execute: (operation) => operation(),
            error: (ex: any) => this._error(`setItem: failed. ${ex.message}`),
          },

          // execute failed, attempt to free space by removing older entries
          {
            name: 'clearOlderEntries',
            canRetry: (args) => args.errorName === 'QuotaExceededError',
            execute: (retry) => {
              this._cacheLoggerStorage.clear(this._exceededQuotaMaxAgeMs);
              return retry();
            },
            success: () => this._info('setItem: Cleared older entries'),
            error: (ex: any, strategy) =>
              this._warn(`setItem: Strategy '${strategy}' failed. ${ex.message}`),
          },
        ],
        failure: () => {
          throw new Error('All strategies failed.');
        },
      }),
    });

    // cleanup cache
    tickService.everyHour$.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      this._cacheLoggerStorage.clear(this._maxAgeMs);
    });

    userEmailService.latestEmail$.pipe(takeUntil(this._onDestroy$)).subscribe((email) => {
      this._id$.next(createStorageId(email));
      this._loadCache(this._id$.getValue());
    });

    userSettings.whenReady$
      .pipe(
        switchMap(() => userSettings.valueChanges$),
        takeUntil(this._onDestroy$),
      )
      .subscribe(([prev, cur]) => {
        if (prev.loggingCacheEnabled && !cur.loggingCacheEnabled) {
          this.clearCache();
        }
      });

    let prevLength = 0;
    tickService.everyTenSeconds$
      .pipe(withLatestFrom(this._cache$), takeUntil(this._onDestroy$))
      .subscribe(([, cache]) => {
        const curLength = cache.length;
        if (curLength !== prevLength) {
          this._saveCache();
        }
        prevLength = curLength;
      });
  }

  public getLogs() {
    return this._cache$.getValue();
  }

  public serializeLogs() {
    return this._cacheLoggerStorage.getItem(this._id$.getValue());
  }

  public clearCache() {
    const id = this._id$.getValue();
    this._cacheLoggerStorage.removeItem(id);
    this._cache$.next([]);
  }

  public cacheItem(
    type: LogType,
    message: string,
    properties?: { [name: string]: string },
    options?: LoggerOptions,
  ) {
    if (this._canCacheItem(options)) {
      const cache = this._cache$.getValue();
      this._cache$.next([...cache, { type, message, properties, timestamp: new Date() }]);
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  private _canCacheItem(options?: LoggerOptions) {
    return this._id$.getValue() !== '' && this._optionsManager.canLogToCache(options);
  }

  private _loadCache(id: string) {
    try {
      const data = this._cacheLoggerStorage.getItem(id);
      if (data != null) {
        this._cache$.next(JSON.parse(data ?? '[]'));
      }
    } catch (error: any) {
      this._cacheLoggerStorage.removeItem(id);
      this._cache$.next([]);
      this._error('Error loading cache-logger data', { error });
    }
  }

  private _saveCache() {
    const id = this._id$.getValue();
    try {
      const str = JSON.stringify(this._cache$.getValue());
      this._cacheLoggerStorage.setItem(id, str);
    } catch (error: any) {
      this._cacheLoggerStorage.removeItem(id);
      this._cache$.next([]);
      this._error('Error saving cache-logger data', { error });
    }
  }

  private _info(message: string, props?: any) {
    this._insightsLogger.info(`${this.constructor.name}: ${message}`, createSafeProps(props));
    this._newRelicLogger.info(`${this.constructor.name}: ${message}`, createSafeProps(props));
  }

  private _warn(message: string, props?: any) {
    this._insightsLogger.warning(`${this.constructor.name}: ${message}`, createSafeProps(props));
    this._newRelicLogger.warning(`${this.constructor.name}: ${message}`, createSafeProps(props));
  }

  private _error(message: string, props?: any) {
    this._insightsLogger.error(`${this.constructor.name}: ${message}`, createSafeProps(props));
    this._newRelicLogger.error(`${this.constructor.name}: ${message}`, createSafeProps(props));
  }
}
